import { AnyAction } from 'redux';
import {
  AdressvorschlagFailedPayload,
  AdressvorschlagPayload,
  CALLER_ID,
  GlobalActions,
  ProvideVorhandeneVwgValidationPayload,
  RoleDeterminedPayload,
  StreckenTarifierungRequestEvents,
} from '@ps20-frontend/shop-integration-redux';
import { ownPricingErrorReducer, OwnPricingEvents } from '@ps20-frontend/reducers';
import {
  App,
  BaseTransientState,
  FeatureToggles,
  MapViewModal,
  MultiangebotAdressenValidationResult,
  Regexps,
  Rolle,
  VorvertragVorschaeden,
} from '@ps20-frontend/frontend-models';
import { VorschadenArt } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { VwgActions } from '../vwgActions';
import { ExtremeWetterschutzSelbstbeteiligungValues } from '../models/ExtremWetterschutzSelbstbeteiligungValues.model';

export interface TransientState extends BaseTransientState, Rolle {
  adressvorschlagIsInProgress: boolean;
  vwgVorhandenError: MultiangebotAdressenValidationResult;
  vorvertragVorschaeden?: VorvertragVorschaedenVwg;
  isRisikoortVnAdresse?: boolean;
  mapViewModal: MapViewModal;
  showZumAngebotLoadingSpinner: boolean;
  exetremeWetterschutzSelbsbeteiligungValuesList?: ExtremeWetterschutzSelbstbeteiligungValues[];
}

export interface VorvertragVorschaedenVwg extends Record<string, number> {
  elementar: number;
  feuer: number;
  leitungswasser: number;
  sturmhagel: number;
}

const ownPricingEvents: OwnPricingEvents = {
  failed: StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_VWG,
  successful: [
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_VWG,
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_VWG,
  ],
};

export const transientStateReducer = function (
  sectionState: TransientState = null,
  action: AnyAction,
  appState: IAppState,
): TransientState {
  switch (action.type) {
    case VwgActions.ZURUECK_ZUM_SHOP_MODAL_OPEN: {
      return { ...sectionState, zurueckZumShopModalOpen: true };
    }
    case VwgActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE: {
      return { ...sectionState, zurueckZumShopModalOpen: false };
    }
    case GlobalActions.FEATURE_TOGGLE_RECEIVED: {
      const actionPayload: FeatureToggles = action.payload;
      return { ...sectionState, featureToggles: actionPayload };
    }
    case GlobalActions.TARIFIERUNG_HAS_STARTED: {
      return { ...sectionState, tarifierungIsInProgress: true };
    }
    case GlobalActions.TARIFIERUNG_HAS_ENDED: {
      return { ...sectionState, tarifierungIsInProgress: false };
    }
    case GlobalActions.TARIFIERUNG_CANCELED: {
      return { ...sectionState, tarifierungIsInProgress: false };
    }
    case VwgActions.PLZ_CHANGED: {
      return {
        ...sectionState,
        adressvorschlagIsInProgress: true,
        vwgVorhandenError: null,
        mapViewModal: {
          ...sectionState.mapViewModal,
          isAdresseTarifierbar: true,
        },
      };
    }
    case VwgActions.STRASSE_CHANGED:
    case VwgActions.HAUSNUMMER_CHANGED: {
      return { ...sectionState, vwgVorhandenError: null };
    }
    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      const angebotsId = appState.angebotsId;
      const callerId = `${CALLER_ID.VWG_BASISDATEN}_${angebotsId}`;
      if (payload.callerId !== callerId) {
        return sectionState;
      }
      return { ...sectionState, adressvorschlagIsInProgress: false };
    }
    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      const angebotsId = appState.angebotsId;
      const callerId = `${CALLER_ID.VWG_BASISDATEN}_${angebotsId}`;
      if (payload.callerId !== callerId) {
        return sectionState;
      }
      return { ...sectionState, adressvorschlagIsInProgress: false };
    }
    case GlobalActions.DO_REQUEST:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_VWG:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_VWG:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_VWG:
      return ownPricingErrorReducer(
        sectionState,
        action,
        { appId: App.VWG, angebotsId: appState?.angebotsId },
        ownPricingEvents,
      );
    case GlobalActions.SET_PRICE_GUARANTEE: {
      return {
        ...sectionState,
        priceGuarantee: action.payload,
      };
    }
    case GlobalActions.CHANGE_RELEVANT_RISKDATA_HASH: {
      return {
        ...sectionState,
        priceGuarantee: false,
      };
    }
    case GlobalActions.ROLE_DETERMINED: {
      const actionPayload: RoleDeterminedPayload = action.payload;
      return {
        ...sectionState,
        role: actionPayload.role,
      };
    }
    case GlobalActions.PROVIDE_MULTIANGEBOT_ADRESS_VALIDATION: {
      const { appId, angebotsId, validationResult }: ProvideVorhandeneVwgValidationPayload =
        action.payload;
      if (appId === App.VWG && angebotsId === appState.angebotsId) {
        return {
          ...sectionState,
          vwgVorhandenError: validationResult,
        };
      }
      return sectionState;
    }
    case GlobalActions.VORVERTRAG_VORSCHAEDEN_ERMITTELT: {
      const actionPayload: Array<VorvertragVorschaeden> = action.payload;
      const vorvertragVorschaeden = actionPayload.filter(
        vvVorschaeden =>
          vvVorschaeden.appId === App.VWG && vvVorschaeden.angebotsId === appState.angebotsId,
      );

      if (vorvertragVorschaeden?.length > 0) {
        const elementar = vorvertragVorschaeden.find(
          vv => vv.vorschaedenArt === VorschadenArt.ELEMENTAR,
        )?.anzahlVorschaeden;
        const feuer = vorvertragVorschaeden.find(
          vv => vv.vorschaedenArt === VorschadenArt.FEUER,
        )?.anzahlVorschaeden;
        const lw = vorvertragVorschaeden.find(
          vv => vv.vorschaedenArt === VorschadenArt.LEITUNGSWASSER,
        )?.anzahlVorschaeden;
        const sturm = vorvertragVorschaeden.find(
          vv => vv.vorschaedenArt === VorschadenArt.STURM_HAGEL,
        )?.anzahlVorschaeden;

        return {
          ...sectionState,
          vorvertragVorschaeden: {
            elementar: elementar || 0,
            feuer: feuer || 0,
            leitungswasser: lw || 0,
            sturmhagel: sturm || 0,
          },
        };
      } else {
        return sectionState;
      }
    }
    case GlobalActions.OPEN_MAP_VIEW_MODAL: {
      if (action.payload.appId === App.VWG && action.payload.angebotsId === appState.angebotsId) {
        return {
          ...sectionState,
          mapViewModal: {
            isOpen: true,
            isAdresseTarifierbar:
              action.payload.adresseTarifierbar ?? sectionState.mapViewModal.isAdresseTarifierbar,
            origin: action.payload.origin ? action.payload.origin : null,
          },
        };
      }
      return sectionState;
    }
    case GlobalActions.CLOSE_MAP_VIEW_MODAL: {
      return {
        ...sectionState,
        mapViewModal: {
          ...sectionState.mapViewModal,
          isOpen: false,
        },
      };
    }
    case GlobalActions.REGEXPS_RECEIVED: {
      const actionPayload: Regexps = action.payload;
      return { ...sectionState, regexps: actionPayload };
    }
    case GlobalActions.SETTINGS_RECEIVED:
      return {
        ...sectionState,
        settings: action.payload,
      };
    case GlobalActions.KOOPERATIONEN_LIST_RECEIVED: {
      return {
        ...sectionState,
        kooperationen: action.payload,
      };
    }
    case GlobalActions.SET_EDITING_UW_APPROVED_OFFER: {
      return { ...sectionState, ...action.payload };
    }
    case VwgActions.SHOW_ZUM_ANGEBOT_LOADING_SPINNER: {
      return {
        ...sectionState,
        showZumAngebotLoadingSpinner: action.payload,
      };
    }
    case VwgActions.EXETREMWETTERSCHUTZ_SELBSTBETEILIGUNG_VALUES_RECEIVED: {
      if (appState.angebotsId === action.payload.angebotsId) {
        return {
          ...sectionState,
          exetremeWetterschutzSelbsbeteiligungValuesList: action.payload.values,
        };
      } else {
        return sectionState;
      }
    }
    default:
      return sectionState;
  }
};
